import { TabStyles } from '@tableau/widgets';

import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { imgUrl } from '~src/commonInfra/utils/ImgUrl';

import { cssClass, Style, textEllipsis } from '../../../style';

export const THUMBNAIL_HEIGHT = 184;
const THUMBNAIL_BORDER_HEIGHT = 2; // 1px top and bottom
const CARD_INFO_HEIGHT = 41;

const NUM_LINES_TEXT_TO_SHOW = 2;
const LINE_HEIGHT_IN_EM = 1.5; // from .tb-app line-height
const PRIMARY_FONT_SIZE = 14;
const DESCRIPTION_FONT_SIZE = TabStyles.Typography.Sizing.Default;
export const CARD_DESCRIPTION_HEIGHT = NUM_LINES_TEXT_TO_SHOW * DESCRIPTION_FONT_SIZE * LINE_HEIGHT_IN_EM;

export const TOTAL_CARD_HEIGHT = THUMBNAIL_HEIGHT + THUMBNAIL_BORDER_HEIGHT + CARD_INFO_HEIGHT + CARD_DESCRIPTION_HEIGHT;
export const TOTAL_CARD_WIDTH = 300;

const INFO_IMAGE_URL = imgUrl('data_prep_info.svg');
const BOX_SHADOW = `0px 1px 2px rgba(0,0,0,.15)`;
const BORDER = '1px solid #d8d8d8';
const CARD_BUTTON_DIMENSION = 30;

const VIEWS_IMAGE_URL = imgUrl('spaces_card_views.svg');

// "Application Blue" with alpha for selection/hover
const HIGHLIGHT_COLOR = 'rgba(34, 38, 54, 0.5)';

const cssPrefix = 'CardView';

export namespace CardViewStyles {

  const BaseCardStyle: Style = {
    fontWeight: 'normal',
    position: 'relative',
    // Many thumbnail elements have a hardcoded positive z-index; explicitly declaring 0 here
    // forces a new stacking context so the thumbnail doesn't float above other elements such as dropdowns
    zIndex: 0,
    height: TOTAL_CARD_HEIGHT,
    width: TOTAL_CARD_WIDTH,
    fontSize: TabStyles.Typography.Sizing.Default,
    overflow: 'hidden'
  };

  export const CardWrapperFocusClass = cssClass(cssPrefix, 'card-wrapper-focus', {
    $nest: {
      '&:focus': {
        boxShadow: CommonStyles.Focus.Indicator.boxShadow,
      }
    }
  });

  export const SelectCheckbox = cssClass(cssPrefix, 'select-checkbox', {
    position: 'absolute',
    top: 10,
    left: 7,
    zIndex: 5
  });

  const ThumbnailBaseStyle: Style = {
    display: 'block',
    height: THUMBNAIL_HEIGHT,
    backgroundRepeat: 'no-repeat',
    borderRadius: 1,
    border: BORDER,
    boxShadow: BOX_SHADOW,
    position: 'relative',
    zIndex: 3,
  };

  export const ThumbnailPreloaded = cssClass(cssPrefix, 'thumbnail-preloaded', ThumbnailBaseStyle);

  export const Thumbnail = cssClass(cssPrefix, 'thumbnail', ThumbnailBaseStyle, {
    backgroundColor: '#fafafa',
    backgroundPosition: 'center',
  });

  export const ThumbnailCenteredText = cssClass(cssPrefix, 'thumbnail-centered-text', {
    color: CommonStyles.Colors.Grey51,
    fontSize: '14px',
    left: '50%',
    position: 'absolute',
    top: '50%',
    transform: 'translateX(-50%) translateY(-50%)',
    '-ms-transform': 'translateX(-50%) translateY(-50%)',
  });

  export const Card = cssClass(cssPrefix, 'card-inner', BaseCardStyle, {
    $nest: {
      '&>span': {
        display: 'block'
      }
    }
  });

  export const ProjectCardShape = cssClass(cssPrefix, 'project-card-shape', {
    position: 'absolute',
    zIndex: 4,
    pointerEvents: 'none',
    top: '-4px',
    right: '-4px'
  });

  export const RecommendationCard = cssClass(cssPrefix, 'recommendation-card', {
    overflow: 'hidden'
  });

  const RecommendationCardShapeBase: Style =  {
    position: 'absolute',
    zIndex: 4,
    pointerEvents: 'none',
    right: '-57px',

    transition: 'all .5s ease'
  };

  export const RecommendationCardShape = cssClass(cssPrefix, 'recommendation-card-shape', {
    ...RecommendationCardShapeBase
  });

  export const RecommendationCardDCRShapeEn = cssClass(cssPrefix, 'recommendation-card-shape', {
    ...RecommendationCardShapeBase,
    right: '-80px'
  });

  export const RecommendationCardForYouShapeLong = cssClass(cssPrefix, 'recommendation-card-shape', {
    ...RecommendationCardShapeBase,
    right: '-117px'
  });

  export const RecommendationCardMidShape = cssClass(cssPrefix, 'recommendation-card-shape', {
    ...RecommendationCardShapeBase,
    right: '-127px'
  });

  export const RecommendationCardLongShape = cssClass(cssPrefix, 'recommendation-card-shape', {
    ...RecommendationCardShapeBase,
    right: '-157px'
  });

  export const RecommendationCardShapeHover = cssClass(cssPrefix, 'recommendation-card-shape-hover', {
    ...RecommendationCardShapeBase,
    right: '0'
  });

  const RecommendationCardTextBase: Style = {
    fontWeight: 500,
    position: 'absolute',
    zIndex: 5,
    right: '-115px',
    top: '6px',
    pointerEvents: 'none',
    fontSize: TabStyles.Typography.Sizing.Small,
    color: TabStyles.Typography.Color.InvertedPrimary,

    transition: 'all .5s ease'
  };

  export const RecommendationCardText = cssClass(cssPrefix, 'recommendation-card-text', {
    ...RecommendationCardTextBase
  });

  export const RecommendationCardLongText = cssClass(cssPrefix, 'recommendation-card-text', {
    ...RecommendationCardTextBase,
    right: '-138px',
  });

  export const RecommendationCardTextHover = cssClass(cssPrefix, 'recommendation-card-text-hover', {
    ...RecommendationCardTextBase,
    right: '13px'
  });


  // Workbook thumbnails are decorated to look like a stack of cards.
  const ThumbnailWorkbookDecorationBase: Style = {
    display: 'block',
    height: THUMBNAIL_HEIGHT,
    boxShadow: BOX_SHADOW,
    backgroundColor: TabStyles.Colors.White,
    position: 'absolute',
  };

  const WorkbookDecorationSpacing = 9;

  export const ThumbnailWorkbookDecoration = cssClass(cssPrefix, 'thumbnail-workbook-decoration', {
    ...ThumbnailWorkbookDecorationBase,
    width: TOTAL_CARD_WIDTH - (WorkbookDecorationSpacing * 2),
    top: WorkbookDecorationSpacing,
    left: WorkbookDecorationSpacing,
    zIndex: 1,
    $nest: {
      '&::before': {
        ...ThumbnailWorkbookDecorationBase,
        content: `''`,
        width: TOTAL_CARD_WIDTH - WorkbookDecorationSpacing,
        top: -4,
        left: -4
      }
    }
  });

  export const SelectOverlay = cssClass(cssPrefix, 'card-select-overlay', {
    display: 'none'
  });

  export const CardHighlight = cssClass(cssPrefix, 'card-highlight', {
    $nest: {
      [`.${SelectOverlay}`]: {
        display: 'block',
        position: 'absolute',
        height: THUMBNAIL_HEIGHT,
        width: '100%',
        top: 0,
        left: 0,
        zIndex: 4,
        backgroundColor: HIGHLIGHT_COLOR,
        borderRadius: 1,
        pointerEvents: 'none'
      }
    }
  });

  const customThumbnailBase: Style = {
    height: THUMBNAIL_HEIGHT,
    backgroundColor: '#fafafa',
    boxShadow: BOX_SHADOW,
    borderRadius: 1,
    border: BORDER,
    position: 'relative',
    zIndex: 3,

    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    alignItems: 'center',

    boxSizing: 'border-box',
    paddingTop: TabStyles.Spacing.LargeSpace,
    paddingBottom: TabStyles.Spacing.LargeSpace,
    justifyContent: 'center',

    color: TabStyles.Typography.Color.PrimaryLight
  };

  export const CustomThumbnail = cssClass(cssPrefix, 'custom-thumbnail', {
    ...customThumbnailBase
  });

  export const CustomThumbnailHover = cssClass(cssPrefix, 'custom-thumbnail-hover', {
    ...customThumbnailBase,
    cursor: 'pointer',
    backgroundColor: HIGHLIGHT_COLOR
  });

  export const CustomThumbnailHoverNoPointer = cssClass(cssPrefix, 'custom-thumbnail-hover', {
    ...customThumbnailBase,
    cursor: 'default',
    backgroundColor: HIGHLIGHT_COLOR
  });

  const customThumbnailImgBase: Style = {
    width: 60,
    height: 60,
    marginBottom: TabStyles.Spacing.SmallSpace,
    marginLeft: 'auto',
    marginRight: 'auto'
  };

  export const CustomThumbnailImg = cssClass(cssPrefix, 'custom-thumbnail-img', {
    ...customThumbnailImgBase
  });

  export const DatabaseCustomThumbnailImg = cssClass(cssPrefix, 'database-custom-thumbnail-img', {
    ...customThumbnailImgBase,
    marginBottom: TabStyles.Spacing.ExtraLargeSpace
  });

  export const DatabaseCustomSecondTitle = cssClass(cssPrefix, 'database-custom-thumbnail-second-title', {
    fontSize: DESCRIPTION_FONT_SIZE,
    color: TabStyles.Typography.Color.PrimaryLight,
  });

  export const DatasourceCustomThumbnailImg = cssClass(cssPrefix, 'datasource-custom-thumbnail-img', {
    ...customThumbnailImgBase,
    marginBottom: TabStyles.Spacing.ExtraLargeSpace
  });

  export const CustomThumbnailTopContainer = cssClass(cssPrefix, 'custom-thumbnail-top-container', {
    display: 'block',
    margin: 'auto',
    textAlign: 'center',
    marginBottom: TabStyles.Spacing.MediumSpace,
    fontSize: PRIMARY_FONT_SIZE,
    color: TabStyles.Typography.Color.Primary
  });

  export const DataconnectionThumbnailDatasourceLink = cssClass(cssPrefix, 'dataconection-thumbnail-datasource-link', {
    marginBottom: TabStyles.Spacing.SmallSpace,
    marginLeft: TabStyles.Spacing.MediumSpace,
    marginRight: TabStyles.Spacing.MediumSpace
  });

  export const DataconnectionThumbnailConnectionType = cssClass(cssPrefix, 'dataconnection-thumbnail-connection-type', {
    marginBottom: TabStyles.Spacing.SmallSpace,
    color: TabStyles.Typography.Color.Primary,
    fontSize: PRIMARY_FONT_SIZE
  });

  export const DataconnectionThumbnailUsername = cssClass(cssPrefix, 'dataconnection-thumbnail-username', {
    fontStyle: 'italic',
    color: TabStyles.Typography.Color.PrimaryLight
  });

  export const ActionRow = cssClass(cssPrefix, 'action-row', {
    display: 'flex',
    height: CARD_INFO_HEIGHT,
    justifyContent: 'space-between',
    paddingLeft: TabStyles.Spacing.MediumSpace,
    paddingRight: TabStyles.Spacing.SmallSpace,
    $nest: {
      '&>*': {
        height: CARD_BUTTON_DIMENSION,
        marginTop: TabStyles.Spacing.MediumSpace,
        paddingRight: TabStyles.Spacing.DefaultSpace
      }
    }
  });

  const ActionRowNameStyle = {
    fontWeight: 500,
    flex: '1',
    fontSize: PRIMARY_FONT_SIZE,
    paddingRight: TabStyles.Spacing.MediumSpace,
    paddingTop: TabStyles.Spacing.SmallSpace
  };

  export const ActionRowName = cssClass(cssPrefix, 'action-row-name', textEllipsis, ActionRowNameStyle);

  export const ActionRowNameMissing = cssClass(cssPrefix, 'action-row-name-missing', textEllipsis, {
    ...ActionRowNameStyle,
    fontStyle: 'italic',
    color: CommonStyles.Colors.Grey178
  });

  export const ActionRowInfo = cssClass(cssPrefix, 'action-row-info', {
    backgroundImage: INFO_IMAGE_URL,
    height: '18px',
    width: '18px',
    display: 'inline-block',
    backgroundRepeat: 'no-repeat',
    padding: '3px',
    backgroundPosition: '3px 3px',
    backgroundSize: '18px 18px',
    $nest: {
      '&:hover': {
        cursor: 'pointer'
      }
    }
  });

  export const CardDescription = cssClass(cssPrefix, 'card-description', {
    fontSize: DESCRIPTION_FONT_SIZE,
    color: TabStyles.Typography.Color.PrimaryLight,
    overflow: 'hidden',
    position: 'relative',
    maxHeight: CARD_DESCRIPTION_HEIGHT,
    paddingRight: TabStyles.Spacing.MediumSpace,
    paddingLeft: TabStyles.Spacing.MediumSpace,
    textOverflow: 'ellipsis',
  });

  export const CardLastAccessedAt = cssClass(cssPrefix, 'card-last-accessed-at', {
    fontSize: DESCRIPTION_FONT_SIZE,
    color: TabStyles.Typography.Color.PrimaryLight,
    overflow: 'hidden',
    position: 'relative',
    maxHeight: CARD_DESCRIPTION_HEIGHT,
    paddingRight: 27,
    paddingLeft: TabStyles.Spacing.MediumSpace,
  });

  export const CardTextAndLinkMaxWidth = TOTAL_CARD_WIDTH - 18; // (minus the padding)
  export const CardTextAndLink = cssClass(cssPrefix, 'card-text-and-link', {
    textOverflow: 'ellipsis',
    maxWidth: CardTextAndLinkMaxWidth,
    paddingRight: 9,
    paddingLeft: 9,
    whiteSpace: 'nowrap',
    overflow: 'hidden'
  });

  export const AnchorUnderlay = cssClass(cssPrefix, 'anchor-underlay', {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  });

  export const VizRecViewsIcon = cssClass(cssPrefix, 'vizrec-views-icon', {
    display: 'inline-block',
    backgroundImage: VIEWS_IMAGE_URL,
    backgroundRepeat: 'no-repeat',
    backgroundPositionY: '2px',
    backgroundSize: '19px 19px',
    width: '18px',
    height: '18px'
  });

  const RecCardCommon = {
    color: CommonStyles.Colors.NormalTextColor,
    fontSize: TabStyles.Typography.Sizing.Small,
    display: 'inline-block',
    width: 'auto',
    paddingTop: TabStyles.Spacing.SmallSpace,
    fontVariantNumeric: 'tabular-nums'
  };

  export const VizRecViewsCount = cssClass(cssPrefix, 'vizrec-views-count', {
    ...RecCardCommon,
    marginLeft: TabStyles.Spacing.DefaultSpace,
    marginRight: '10px'
  });

  export const VizRecFavoritesCount = cssClass(cssPrefix, 'vizrec-favorites-count', {
    ...RecCardCommon,
    marginLeft: '-2px',
    marginRight: TabStyles.Spacing.MediumSpace,
  });

  export const SharedWithMeContent = cssClass(cssPrefix, 'shared-with-me-content', {
    display: 'flex',
    paddingLeft: TabStyles.Spacing.MediumSpace,
    paddingRight: TabStyles.Spacing.MediumSpace,
  });

  export const SharedWithMeProfilePicture = cssClass(cssPrefix, 'shared-with-me-profile-picture', {
    width: '34px',
    height: '34px',
    paddingRight: '8px',
  });

  export const SharedWithMeSharerAndText = cssClass(cssPrefix, 'shared-with-me-sharer-and-text', {
    flexDirection: 'column',
  });

  export const SharedWithMeSingleLine = cssClass(cssPrefix, 'shared-with-me-single-line', {
    display: 'flex',
    height: '18px',
    width: '230px',
  });

  export const SharedWithMeSharerNameSingleLine = cssClass(cssPrefix, 'shared-with-me-sharer-name-single-line', {
    flexShrink: 1,
    marginRight: TabStyles.Spacing.SmallSpace,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  });

  export const CollectionCardInfoLayout = cssClass(cssPrefix, 'card-info-layout', {
    position: 'absolute',
    zIndex: 4,
    top: '1px',
    left: '1px',
    width: TOTAL_CARD_WIDTH - 2,
    height: THUMBNAIL_HEIGHT,
    pointerEvents: 'none',
    display: 'flex',
    flexDirection: 'column'
  });

  const CollectionCardTypeLabelAreaBase = {
    paddingLeft: TabStyles.Spacing.MediumSpace,
    paddingRight: TabStyles.Spacing.MediumSpace,
    height: '30px',
    display: 'flex',
    alignItems: 'center',
  };

  export const CollectionCardTypeLabelArea = cssClass(cssPrefix, 'card-type-label-area', {
    ...CollectionCardTypeLabelAreaBase,
    backgroundColor: TabStyles.Colors.White,
  });

  export const CollectionCardTypeLabelAreaHovered = cssClass(cssPrefix, 'card-type-label-area', {
    ...CollectionCardTypeLabelAreaBase,
  });

  export const CollectionCardTypeLabel = cssClass(cssPrefix, 'card-type-label', {
    fontWeight: 500,
    fontSize: TabStyles.Typography.Sizing.Default,
    color: CommonStyles.Colors.Grey102,
    letterSpacing: '0.5px',
  });

  export const CollectionCardTitleArea = cssClass(cssPrefix, 'card-title-area', {
    paddingLeft: TabStyles.Spacing.MediumSpace,
    paddingRight: TabStyles.Spacing.MediumSpace,
    paddingTop: '16px',
  });

  export const CollectionCardTitleText = cssClass(cssPrefix, 'card-title-text', {
    fontSize: '34px',
    color: CommonStyles.Colors.Grey51,
    lineHeight: '44px',
    maxHeight: '88px',
    overflow: 'hidden'
  });

  export const PrivateCollectionArea = cssClass(cssPrefix, 'card-private-collection-area', {
    padding: TabStyles.Spacing.MediumSpace
  });

  export const PrivateCollectionText = cssClass(cssPrefix, 'card-private-collection-label', {
    paddingLeft: TabStyles.Spacing.DefaultSpace,
    fontWeight: 500,
    fontSize: TabStyles.Typography.Sizing.Default,
    color: CommonStyles.Colors.Grey51,
    letterSpacing: '0.5px',
  });
}
